import { useState } from 'react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import { X, FileText } from 'lucide-react';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

/**
 * SimplePDFViewer - A lightweight, reliable PDF viewer
 * Displays a PDF document with built-in zoom and navigation.
 * Opens directly on the specified page (initialPage prop, 0-based index).
 */
const SimplePDFViewer = ({
  documentUrl,
  initialPage = 0,
  onClose
}) => {
  const [numPages, setNumPages] = useState(0);

  // PDF viewer plugin with toolbar (hide default sidebar)
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: () => [],
  });

  const { jumpToPage } = defaultLayoutPluginInstance;

  // Navigate to the target page once the PDF is loaded
  const handleDocumentLoad = (e) => {
    setNumPages(e.doc.numPages);
    if (initialPage > 0 && jumpToPage) {
      setTimeout(() => jumpToPage(initialPage), 100);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-75 flex items-center justify-center">
      <div className="relative w-full h-full bg-white flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Visualiseur PDF
            </h2>
            {numPages > 0 && (
              <span className="text-sm text-gray-500">
                ({numPages} pages)
              </span>
            )}
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
            title="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* PDF Viewer - full width */}
        <div className="flex-1 overflow-auto bg-gray-100">
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
            <div className="h-full">
              <Viewer
                fileUrl={documentUrl}
                plugins={[defaultLayoutPluginInstance]}
                initialPage={initialPage}
                onDocumentLoad={handleDocumentLoad}
                renderError={(error) => (
                  <div className="flex items-center justify-center h-full p-8">
                    <div className="text-center max-w-md">
                      <div className="text-red-600 mb-4">
                        <FileText className="h-16 w-16 mx-auto mb-2 opacity-50" />
                        <h3 className="text-lg font-semibold mb-2">Erreur de chargement du PDF</h3>
                        <p className="text-sm text-gray-600">{error.message}</p>
                      </div>
                      <p className="text-xs text-gray-500 mt-4">
                        Vérifiez que le fichier est accessible et au format PDF valide.
                      </p>
                    </div>
                  </div>
                )}
              />
            </div>
          </Worker>
        </div>
      </div>
    </div>
  );
};

export default SimplePDFViewer;
